// Settings page
import React from "react";
import {
  Moon,
  Sun,
  Bell,
  Database,
  Zap,
  Info,
  CheckCircle,
} from "lucide-react";
import { useUIStore } from "../store";

export const Settings: React.FC = () => {
  const { theme, toggleTheme } = useUIStore();

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Settings
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage your application preferences and configuration
          </p>
        </div>
      </div>

      {/* Settings Sections */}
      <div className="space-y-6">
        {/* Appearance */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <Sun className="h-5 w-5 mr-2" />
              Appearance
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Customize how the application looks
            </p>
          </div>
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <label
                  htmlFor="theme-toggle"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Theme
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Choose between light and dark mode
                </p>
              </div>
              <button
                id="theme-toggle"
                onClick={toggleTheme}
                className="relative inline-flex h-10 w-20 items-center rounded-full bg-gray-200 dark:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <span
                  className={`inline-flex h-8 w-8 items-center justify-center transform rounded-full bg-white dark:bg-gray-900 shadow-lg transition-transform ${
                    theme === "dark" ? "translate-x-11" : "translate-x-1"
                  }`}
                >
                  {theme === "dark" ? (
                    <Moon className="h-4 w-4 text-blue-500" />
                  ) : (
                    <Sun className="h-4 w-4 text-yellow-500" />
                  )}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <Bell className="h-5 w-5 mr-2" />
              Notifications
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Configure how you receive updates
            </p>
          </div>
          <div className="px-6 py-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Workflow Updates
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Get notified when workflows complete or fail
                </p>
              </div>
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                  Real-time via WebSocket
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  System Alerts
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Important system messages and errors
                </p>
              </div>
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                  Enabled
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Performance */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <Zap className="h-5 w-5 mr-2" />
              Performance
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Optimize application performance
            </p>
          </div>
          <div className="px-6 py-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Auto-refresh Dashboard
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Automatically refresh workflow data
                </p>
              </div>
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                  Via WebSocket
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Static Asset Caching
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Cache CSS, JS, and images locally
                </p>
              </div>
              <div className="flex items-center">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Disabled (Development)
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Data & Storage */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <Database className="h-5 w-5 mr-2" />
              Data & Storage
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manage application data and storage
            </p>
          </div>
          <div className="px-6 py-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Local Storage
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Authentication tokens and preferences
                </p>
              </div>
              <button className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 dark:bg-red-900/20 dark:text-red-400 rounded-md hover:bg-red-200 dark:hover:bg-red-900/30">
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* About */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <Info className="h-5 w-5 mr-2" />
              About
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Application information
            </p>
          </div>
          <div className="px-6 py-4 space-y-3">
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Version
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                1.0.0
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Environment
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Development
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                API Endpoint
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                http://localhost:8000
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

